/**
 * «Додаткові матеріали» — файли, які замовник прикріпив до вакансії
 * (owner 2026-09-12: «хочу, щоб картинки, відео і файли були не тільки в боті,
 * але й в аппці, між блоком тексту вакансії і блоком контактів замовника»).
 *
 * Чому файли тягнемо через `fetch`, а не ставимо в `src` напряму: ендпоінт
 * `/feed/{id}/media/{mid}` вимагає заголовок `X-Telegram-Init-Data`, а тег
 * `<img>` заголовків не шле. Тому качаємо як blob і показуємо object-URL.
 * Фото важать 16-670 КБ, тому це дешево; object-URL відкликаємо на unmount.
 */
import { useEffect, useState } from "react";

import { Icon } from "@/components/Icon";
import type { VacancyMedia } from "@/api/hooks";
import { authedBlobUrl } from "@/api/client";

const tg = () => (window as any).Telegram?.WebApp;

function sizeLabel(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return "менше 1 КБ";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

/** Короткий тип файла: «PDF», «DOCX», «Таблиця». Повний mime у рядку виглядав
 *  як «application/vnd.openxmlformats-officedocument.wordprocessingml.document»
 *  і займав три рядки (owner 2026-09-12). */
function typeLabel(media: VacancyMedia): string {
  const name = (media.filename || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() || "" : "";
  const mime = (media.mime || "").toLowerCase();
  const byExt: Record<string, string> = {
    pdf: "PDF",
    doc: "Документ",
    docx: "Документ",
    xls: "Таблиця",
    xlsx: "Таблиця",
    csv: "Таблиця",
    ppt: "Презентація",
    pptx: "Презентація",
    txt: "Текст",
    rtf: "Текст",
    zip: "Архів",
    rar: "Архів",
    "7z": "Архів",
    ai: "Illustrator",
    psd: "Photoshop",
    fig: "Figma",
  };
  if (byExt[ext]) return byExt[ext];
  if (mime.startsWith("image/")) return "Зображення";
  if (mime.startsWith("video/")) return "Відео";
  if (mime.startsWith("audio/")) return "Аудіо";
  return ext ? ext.toUpperCase() : "Файл";
}

/** Як показувати файл: фото — картинкою, відео — плеєром.
 *
 *  Страховка на випадок, коли зображення приїхало ДОКУМЕНТОМ (замовник
 *  надіслав його в Telegram файлом, а не фото): `kind` тоді `document`, хоч
 *  mime — `image/*`. Дивимось на тип вмісту, а не на `kind`.
 *  (Скрін власника 14.09, де фото показувались рядками, мав іншу причину —
 *  CSP на проді рубав `blob:`; це виправлено в Caddyfile.) */
function effKind(media: VacancyMedia): string {
  if (media.kind === "photo" || media.kind === "video") return media.kind;
  const mime = (media.mime || "").toLowerCase();
  const ext = (media.filename || "").toLowerCase().split(".").pop() || "";
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "heic", "gif"].includes(ext)) {
    return "photo";
  }
  if (mime.startsWith("video/") || ["mp4", "mov", "m4v", "webm"].includes(ext)) {
    return "video";
  }
  return "document";
}

/** Один файл: тягнемо blob і показуємо за типом. */
function MediaItem({
  media,
  onOpen,
}: {
  media: VacancyMedia;
  onOpen: (url: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    let alive = true;
    let objUrl: string | null = null;
    authedBlobUrl(media.url)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        objUrl = u;
        setUrl(u);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [media.url]);

  if (failed) {
    return (
      <div className="flex h-24 items-center justify-center rounded-[0.75rem] bg-ink/[0.04] text-[0.6875rem] text-ink-faint">
        не завантажилось
      </div>
    );
  }

  if (effKind(media) === "photo" && !broken) {
    return (
      <button
        type="button"
        onClick={() => url && onOpen(url)}
        className="block h-24 w-full overflow-hidden rounded-[0.75rem] bg-ink/[0.04] active:opacity-80"
      >
        {url ? (
          <img
            src={url}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            // Owner 2026-09-12: у одного файла був знак питання — це не фото,
            // а щось інше, збережене як photo. Тепер такий файл показуємо
            // рядком, а не битою картинкою.
            onError={() => setBroken(true)}
          />
        ) : null}
      </button>
    );
  }

  if (effKind(media) === "video") {
    return (
      <div className="col-span-3 overflow-hidden rounded-[0.75rem] bg-black/5">
        {url ? (
          <video src={url} controls preload="metadata" className="w-full" />
        ) : (
          <div className="flex h-32 items-center justify-center text-[0.6875rem] text-ink-faint">
            завантажую відео…
          </div>
        )}
      </div>
    );
  }

  // Документ (або фото, яке не відкрилось): рядок із назвою і кнопкою.
  // Відкриваємо ПІДПИСАНИМ посиланням через `openLink`.
  //
  // Owner 2026-09-12 (друга спроба): «файли так і не відкриваються, максимум
  // пропонує скачати». Дві причини були:
  //  1) сервер віддавав файл як `attachment` — браузер НЕ малює, а качає.
  //     Виправлено на сервері (`content_disposition_type="inline"`).
  //  2) docx/xlsx/pptx не вміє малювати ЖОДЕН браузер. Для них ведемо через
  //     переглядач Google Docs: посилання підписане й живе годину, тому
  //     переглядач може його прочитати сам.
  const openable = media.open_url || "";
  const mime = (media.mime || "").toLowerCase();
  const name = (media.filename || "").toLowerCase();
  const isOffice =
    /officedocument|msword|ms-excel|ms-powerpoint|opendocument/.test(mime) ||
    /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/.test(name);
  const canPreview =
    isOffice ||
    mime === "application/pdf" ||
    /^(image|video|audio|text)\//.test(mime) ||
    /\.(pdf|txt|csv|md)$/.test(name);
  return (
    <div className="col-span-3 flex items-center gap-2.5 rounded-[0.75rem] bg-ink/[0.04] p-3">
      <Icon name="doc" size={16} color="#A8A29C" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[0.78125rem] text-ink">
          {media.filename || "файл"}
        </div>
        <div className="text-[0.6875rem] text-ink-faint">
          {[typeLabel(media), sizeLabel(media.size)].filter(Boolean).join(" · ")}
        </div>
      </div>
      {openable && (
        <button
          type="button"
          onClick={() => {
            tg()?.HapticFeedback?.impactOccurred?.("light");
            const abs = `${window.location.origin}${openable}`;
            const target = isOffice
              ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(abs)}`
              : abs;
            tg()?.openLink
              ? tg().openLink(target)
              : window.open(target, "_blank");
          }}
          className="shrink-0 rounded-full bg-primary/10 px-3 py-1.5 font-display text-[0.71875rem] font-bold text-primary active:scale-[0.98]"
        >
          {canPreview ? "Відкрити" : "Завантажити"}
        </button>
      )}
    </div>
  );
}

export function VacancyMediaBlock({ media }: { media: VacancyMedia[] }) {
  const [full, setFull] = useState<string | null>(null);
  if (!media.length) return null;
  const photos = media.filter((m) => effKind(m) === "photo").length;
  // Owner 2026-09-12: «документи, потім відео, потім знову документи, потім
  // фотки — так нельзя, має бути структура: спершу всі фото, потім усі відео,
  // потім усі файли». Парсер віддає їх у порядку повідомлення, тому сортуємо
  // тут, а `position` лишається як другий ключ — щоб порядок автора всередині
  // групи не ламався.
  const ORDER: Record<string, number> = { photo: 0, video: 1, document: 2 };
  const ordered = [...media].sort(
    (a, b) =>
      // Всередині групи лишаємо порядок, у якому їх віддав API (position).
      (ORDER[effKind(a)] ?? 3) - (ORDER[effKind(b)] ?? 3) || a.id - b.id,
  );
  return (
    <div className="px-5 pt-3">
      <div className="rounded-ios-card bg-surface p-4 shadow-card">
        <div className="mb-3 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim">
            Додаткові матеріали
          </span>
          <span className="ml-auto font-mono text-[0.625rem] text-ink-faint">
            {media.length} шт.
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {ordered.map((m) => (
            <MediaItem key={m.id} media={m} onOpen={setFull} />
          ))}
        </div>
        {photos > 1 && (
          <div className="mt-2 text-[0.6875rem] leading-snug text-ink-faint">
            Натисни на фото, щоб побачити повністю.
          </div>
        )}
      </div>

      {full && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setFull(null)}
        >
          <img
            src={full}
            alt=""
            className="max-h-full max-w-full rounded-[0.75rem] object-contain"
          />
        </div>
      )}
    </div>
  );
}
